/**
 * Button — the canonical variant-driven action button.
 *
 * Consolidates the look of the legacy MSButton (MapleStory gradient +
 * lift-on-hover) and FantasyButton (gradient + brightness-on-hover) into a
 * single token-driven primitive. Those two stay in place so their importers
 * can migrate over time; new UI should reach for THIS.
 *
 * Hover/active are handled the MSButton way — a style swap driven by
 * onMouseEnter/Leave/Down/Up local state — so it stays jsdom-safe and needs
 * no runtime CSS injection.
 */
import React, { useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import {
  COLORS,
  PLAYER_UI,
  RADII,
  SPACE,
  FONT_SIZE,
  FONT_WEIGHT,
  FONTS,
  LETTER_SPACING,
  SHADOW,
  DURATION,
  EASING,
} from '../theme';

export type ButtonVariant =
  | 'brass'
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'success'
  | 'outline'
  | 'ghost';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  iconLeft?: ReactNode;
}

interface VariantStyle {
  base: React.CSSProperties;
  /** Extra props applied while hovered (and not disabled). */
  hover: React.CSSProperties;
}

function variantStyle(variant: ButtonVariant): VariantStyle {
  switch (variant) {
    case 'brass':
      return {
        base: { background: `linear-gradient(135deg, ${PLAYER_UI.brassBright}, ${PLAYER_UI.brass})`,
          color: PLAYER_UI.inkDeep, border: `1px solid ${PLAYER_UI.brassBright}`, boxShadow: SHADOW.sm },
        hover: { background: PLAYER_UI.brassBright, boxShadow: SHADOW.md },
      };
    case 'primary':
      return {
        base: {
          background: `linear-gradient(145deg, ${COLORS.brand}, ${COLORS.brandAccent})`,
          color: COLORS.textPrimary,
          border: 'none',
          boxShadow: SHADOW.sm,
        },
        hover: { filter: 'brightness(1.1)', boxShadow: SHADOW.md },
      };
    case 'secondary':
      return {
        base: {
          background: COLORS.surfaceRaised,
          color: COLORS.textPrimary,
          border: `1px solid ${COLORS.border}`,
          boxShadow: SHADOW.sm,
        },
        hover: { background: COLORS.surfaceHover, borderColor: COLORS.borderStrong },
      };
    case 'danger':
      return {
        base: {
          background: `linear-gradient(145deg, ${COLORS.danger}, ${COLORS.health})`,
          color: COLORS.textPrimary,
          border: 'none',
          boxShadow: SHADOW.sm,
        },
        hover: { filter: 'brightness(1.1)', boxShadow: SHADOW.md },
      };
    case 'success':
      return {
        base: {
          background: `linear-gradient(145deg, ${COLORS.success}, ${COLORS.success})`,
          color: COLORS.textPrimary,
          border: 'none',
          boxShadow: SHADOW.sm,
        },
        hover: { filter: 'brightness(1.1)', boxShadow: SHADOW.md },
      };
    case 'outline':
      return {
        base: {
          background: 'transparent',
          color: COLORS.brand,
          border: `1px solid ${COLORS.brand}`,
          boxShadow: 'none',
        },
        hover: { background: COLORS.surfaceHover },
      };
    case 'ghost':
      return {
        base: {
          background: 'transparent',
          color: COLORS.textSecondary,
          border: '1px solid transparent',
          boxShadow: 'none',
        },
        hover: { background: COLORS.surfaceHover, color: COLORS.textPrimary },
      };
  }
}

function sizeStyle(size: ButtonSize): React.CSSProperties {
  switch (size) {
    case 'xs':
      return { padding: `${SPACE.xs}px ${SPACE.md}px`, fontSize: FONT_SIZE.xs };
    case 'sm':
      return { padding: `${SPACE.sm}px ${SPACE.lg}px`, fontSize: FONT_SIZE.md };
    case 'md':
      return { padding: `${SPACE.md}px ${SPACE.xl}px`, fontSize: FONT_SIZE.lg };
    case 'lg':
      return { padding: `${SPACE.lg}px ${SPACE.xxl}px`, fontSize: FONT_SIZE.xl };
  }
}

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  iconLeft,
  disabled = false,
  style,
  children,
  className,
  ...rest
}: ButtonProps): React.ReactElement {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const v = variantStyle(variant);
  const active = hovered && !disabled;

  const buttonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACE.sm,
    width: fullWidth ? '100%' : 'auto',
    borderRadius: RADII.lg,
    fontFamily: FONTS.ui,
    fontWeight: FONT_WEIGHT.bold,
    letterSpacing: LETTER_SPACING.wide,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: `all ${DURATION.base} ${EASING.standard}`,
    position: 'relative',
    overflow: 'hidden',
    transform: pressed && !disabled ? 'translateY(1px)' : active ? 'translateY(-2px)' : 'translateY(0)',
    ...v.base,
    ...sizeStyle(size),
    ...(active ? v.hover : {}),
    ...style,
  };

  return (
    <button
      className={`helix-button ${className || ''}`}
      type="button"
      style={buttonStyle}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      {...rest}
    >
      {iconLeft != null && <span style={{ display: 'inline-flex', alignItems: 'center' }}>{iconLeft}</span>}
      {children}
    </button>
  );
}
